import { Children, useCallback, useRef, useState } from "react";

/**
 * CarouselRow — swipeable on phones, plain grid from `sm:` up.
 *
 * Admin dashboards accumulate rows of 4 stat tiles; stacked vertically on a
 * 390px screen that becomes an endless scroll. On mobile each row becomes a
 * horizontal snap-scroller with dot indicators; at `sm` and above the same
 * children render as the normal grid defined by `gridClass`.
 */
export function CarouselRow({ children, gridClass = "sm:grid-cols-2 lg:grid-cols-4", itemWidth = "w-[74%]", testId }) {
  const items = Children.toArray(children);
  const ref = useRef(null);
  const [active, setActive] = useState(0);

  const onScroll = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    // RTL reports a negative scrollLeft in WebKit/Blink — normalise it.
    const offset = Math.abs(el.scrollLeft);
    const step = el.scrollWidth / Math.max(items.length, 1);
    setActive(Math.min(items.length - 1, Math.round(offset / step)));
  }, [items.length]);

  const goTo = (i) => {
    const el = ref.current;
    if (!el) return;
    const step = el.scrollWidth / Math.max(items.length, 1);
    // RTL containers scroll into negative territory.
    const rtl = getComputedStyle(el).direction === "rtl";
    el.scrollTo({ left: (rtl ? -1 : 1) * step * i, behavior: "smooth" });
  };

  return (
    <div data-testid={testId}>
      <div
        ref={ref}
        onScroll={onScroll}
        data-testid={testId ? `${testId}-rail` : undefined}
        className={`flex gap-3 overflow-x-auto snap-x snap-mandatory pb-1 -mx-3 px-3 [&::-webkit-scrollbar]:hidden [scrollbar-width:none] sm:mx-0 sm:px-0 sm:grid sm:overflow-visible sm:pb-0 ${gridClass}`}
      >
        {items.map((child, i) => (
          <div key={i} className={`snap-start shrink-0 ${itemWidth} sm:w-auto sm:shrink`}>
            {child}
          </div>
        ))}
      </div>
      {items.length > 1 && (
        <div className="flex justify-center gap-1.5 mt-2 sm:hidden" aria-hidden="true" data-testid={testId ? `${testId}-dots` : undefined}>
          {items.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => goTo(i)}
              className={`h-1.5 rounded-full transition-all ${i === active ? "w-5 bg-primary" : "w-1.5 bg-border"}`}
              tabIndex={-1}
              aria-current={i === active}
              data-testid={testId ? `${testId}-dot-${i}` : undefined}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default CarouselRow;
